export const CHART = {
  width: 408,
  height: 263,
  baseline: 131.5,
  inset: 21,
  rise: 81,
  fall: 83,
  points: 6,
};
export interface ChartPoint {
  x: number;
  y: number;
}

export type ChartZone = 'up' | 'down' | 'neutral';
// Follow the drawn endpoint, including its interpolation across graduation.
// Missing history and an exact baseline must not imply a direction.
export function chartZone(points: ChartPoint[] | null): ChartZone {
  const end = points?.at(-1)?.y;
  if (end === undefined || !Number.isFinite(end) || end === CHART.baseline)
    return 'neutral';
  return end < CHART.baseline ? 'up' : 'down';
}

export const CHART_DOTS = { size: 2, step: 18, inset: 8, rows: 6 };
export const CHART_DOT_WAVE = { scale: 3, ms: 2400, stagger: 180 };
export function chartDotRows(side: 'up' | 'down') {
  return Array.from({ length: CHART_DOTS.rows }, (_, row) => {
    const order = side === 'up' ? CHART_DOTS.rows - 1 - row : row;
    return {
      y: row * CHART_DOTS.step,
      order,
      delay: order * CHART_DOT_WAVE.stagger,
    };
  });
}
export function chartDotGrid(availableWidth: number, scale = 1) {
  const { size, step } = CHART_DOTS;
  const diameter = size * scale;
  const columns =
    Number.isFinite(availableWidth) && Number.isFinite(scale) && scale >= 1
      ? Math.max(0, Math.floor((availableWidth - diameter) / step) + 1)
      : 0;
  return { columns, width: columns ? (columns - 1) * step + diameter : 0 };
}

// Keep actual observations, including both endpoints and the full price range.
// Fill the largest gaps in time to avoid dozens of tiny stairs.
export function chartPoints(values: number[], baseline: number | null) {
  if (
    !baseline ||
    !Number.isFinite(baseline) ||
    baseline < 0 ||
    values.length < 2 ||
    values.some((value) => !Number.isFinite(value) || value <= 0)
  )
    return null;
  let low = 0;
  let high = 0;
  for (let i = 1; i < values.length; i++) {
    if (values[i] < values[low]) low = i;
    if (values[i] > values[high]) high = i;
  }
  // Keep the latest real segment as well, so its highlight never suggests a
  // fabricated observation between the sampled landmarks.
  const indices = [
    ...new Set([0, low, high, values.length - 2, values.length - 1]),
  ].sort((a, b) => a - b);
  while (indices.length < Math.min(CHART.points, values.length)) {
    let gap = 0;
    for (let i = 1; i < indices.length - 1; i++) {
      if (indices[i + 1] - indices[i] > indices[gap + 1] - indices[gap])
        gap = i;
    }
    indices.splice(
      gap + 1,
      0,
      Math.floor((indices[gap] + indices[gap + 1]) / 2),
    );
  }
  const span = Math.max(
    0.02,
    Math.abs(values[low] / baseline - 1),
    Math.abs(values[high] / baseline - 1),
  );
  const points = indices.map((index) => {
    const ratio = values[index] / baseline - 1;
    return {
      x:
        CHART.inset +
        (index / (values.length - 1)) * (CHART.width - 2 * CHART.inset),
      y:
        CHART.baseline -
        (ratio / span) * (ratio >= 0 ? CHART.rise : CHART.fall),
    };
  });
  // Zero-length segments keep the animation topology stable while history grows.
  // They do not add any visible samples or change either endpoint.
  while (points.length < CHART.points)
    points.push({ ...points[points.length - 1] });
  return points;
}

export function chartPath(points: ChartPoint[]) {
  return points
    .map((point, i) =>
      i === 0
        ? `M${point.x.toFixed(2)} ${point.y.toFixed(2)}`
        : `H${point.x.toFixed(2)} V${point.y.toFixed(2)}`,
    )
    .join(' ');
}

export function chartTail(points: ChartPoint[]) {
  const end = points.at(-1);
  if (!end) return null;
  // Short histories are padded with identical endpoints for interpolation.
  const previous = points.findLast((point) => point.x < end.x);
  return previous ? chartPath([previous, end]) : null;
}

// Reserve space for the price chip. Its connector is horizontal at the exact
// latest value; the vertical price step stays to the left, outside the chip.
export function chartToChip(points: ChartPoint[], chipLeft: number) {
  const end = points.at(-1);
  if (!end || !Number.isFinite(chipLeft)) return null;
  const finish = Math.max(CHART.inset, chipLeft - 8);
  const width = CHART.width - 2 * CHART.inset;
  const fitted = points.map((point) => ({
    x: CHART.inset + ((point.x - CHART.inset) / width) * (finish - CHART.inset),
    y: point.y,
  }));
  const connector = ` H${chipLeft.toFixed(2)}`;
  const tail = chartTail(fitted);
  return {
    d: chartPath(fitted) + connector,
    tail: tail ? tail + connector : null,
    end: end.y,
  };
}
